import { __awaiter } from '../../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createContext, useState, useRef, useEffect, useCallback } from 'react';
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api';
import axios from 'axios';
import _debounce from 'lodash/debounce';
import BrandclubStoreConfigUtils from '../../../utils/brandclubStoreConfigUtils.js';
import { getEntityImage } from '../../../utils/assetUtils.js';
import { useGeolocation } from './useGeolocation.js';
import { StoreLocatorWidget as StoreLocatorWidget$1 } from '@brandclub/bc-widgets';

// Create context to share widget state with sub-components
// This allows gradual migration of styled sub-components
const StoreLocatorStyleContext = createContext(null);
const libraries = ['places', 'marker', 'geocoding'];
// Utility to check significant position change
const hasSignificantPositionChange = (oldLat, oldLng, newLat, newLng) => {
    const roundToFour = (num) => Math.round(num * 10000) / 10000;
    return roundToFour(oldLat) !== roundToFour(newLat) || roundToFour(oldLng) !== roundToFour(newLng);
};
// Icons
const SearchIcon = ({ className }) => (jsxs("svg", Object.assign({ className: className, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, { children: [jsx("circle", { cx: "6.5", cy: "6.5", r: "5", stroke: "currentColor", strokeWidth: "1.5" }), jsx("path", { d: "M10.5 10.5L14.5 14.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })] })));
// Helper to get CSS variable value
const getCSSVariable = (varName, fallback = '#000') => {
    if (typeof window === 'undefined')
        return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(varName).trim();
    return value || fallback;
};
// Helper to get color with opacity using CSS variables
const getColorWithOpacity = (varName, opacity, fallback = '#000') => {
    const color = getCSSVariable(varName, fallback);
    // If it's an oklch color, add opacity using modern CSS syntax
    if (color.startsWith('oklch')) {
        // Extract oklch values (format: oklch(0.2669 0.1019 273.44) or oklch(0.2669 0.1019 273.44 / 0.5))
        const match = color.match(/oklch\(([^/)]+)(?:\s*\/\s*[\d.]+)?\)/);
        if (match) {
            return `oklch(${match[1].trim()} / ${opacity})`;
        }
    }
    // For hex colors, convert to rgba
    if (color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // For rgb/rgba colors, extract and modify opacity
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbMatch) {
        return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
    }
    // For oklch or other formats we can't easily convert, use fallback with opacity
    // Convert fallback hex to rgba
    if (fallback.startsWith('#')) {
        const r = parseInt(fallback.slice(1, 3), 16);
        const g = parseInt(fallback.slice(3, 5), 16);
        const b = parseInt(fallback.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return fallback;
};
const CloseIcon = ({ className, onClick }) => {
    const overlayColor = getColorWithOpacity('--color-foreground', 0.5, '#000');
    return (jsxs("svg", Object.assign({ className: className, onClick: onClick, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", style: { cursor: 'pointer' } }, { children: [jsx("circle", { cx: "8", cy: "8", r: "8", fill: overlayColor }), jsx("path", { d: "M5 5L11 11M11 5L5 11", stroke: "white", strokeWidth: "1.5", strokeLinecap: "round" })] })));
};
// RetailerSummaryCard - Horizontal card for bottom carousel
const RetailerSummaryCard = ({ retailerSummary, onClick }) => {
    const nearestStore = retailerSummary.stores[0];
    const nearestDistance = nearestStore === null || nearestStore === void 0 ? void 0 : nearestStore.distance;
    return (jsxs("div", Object.assign({ onClick: onClick, className: "tw:flex-shrink-0 tw:p-2 sm:tw:p-3 tw:flex tw:items-center tw:gap-2 tw:cursor-pointer tw:transition-all hover:tw:shadow-md tw:snap-start tw:touch-manipulation", style: {
            fontFamily: 'Poppins, sans-serif',
            width: 'min(calc(100vw - 100px), 280px)',
            minHeight: '64px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #DCDDDC',
            borderRadius: '8px',
            boxShadow: `0 2px 6px ${getColorWithOpacity('--color-foreground', 0.08, '#000')}`,
        } }, { children: [jsx("div", Object.assign({ className: "tw:w-10 tw:h-10 tw:rounded-full tw:overflow-hidden tw:flex-shrink-0 tw:bg-[#F6F8FA] tw:flex tw:items-center tw:justify-center" }, { children: jsx("img", { src: getEntityImage(retailerSummary.retailerId, 'retailer'), alt: retailerSummary.retailerName, className: "tw:w-full tw:h-full tw:object-contain" }) })), jsxs("div", Object.assign({ className: "tw:flex-1 tw:min-w-0 tw:overflow-hidden" }, { children: [jsx("p", Object.assign({ className: "tw:text-xs tw:font-medium tw:text-primary tw:m-0 tw:leading-[16px] tw:tracking-[0.08px] tw:truncate" }, { children: retailerSummary.retailerName })), jsxs("p", Object.assign({ className: "tw:text-sm tw:font-semibold tw:text-primary tw:m-0 tw:leading-[20px] tw:tracking-[0.08px]" }, { children: [retailerSummary.storeCount, " store", retailerSummary.storeCount !== 1 ? 's' : ''] })), nearestDistance != null && typeof nearestDistance === 'number' && nearestDistance > 0 && (jsxs("p", Object.assign({ className: "tw:text-[11px] tw:font-medium tw:text-primary/70 tw:m-0 tw:leading-[14px] tw:tracking-[0.08px]" }, { children: ["Nearest ", nearestDistance.toFixed(1), " mi"] })))] })), jsx("button", Object.assign({ onClick: (e) => {
                    e.stopPropagation();
                    onClick();
                }, className: "tw:h-[32px] sm:tw:h-[36px] tw:min-h-[32px] sm:tw:min-h-[36px] tw:px-[10px] sm:tw:px-[14px] tw:py-[4px] tw:bg-[#F6F8FA] tw:rounded-[16px] sm:tw:rounded-[18px] tw:border-0 tw:cursor-pointer tw:flex-shrink-0 tw:flex tw:items-center tw:justify-center tw:touch-manipulation" }, { children: jsx("span", Object.assign({ className: "tw:text-[11px] sm:tw:text-xs tw:font-semibold tw:text-primary tw:leading-[16px] sm:tw:leading-[20px] tw:tracking-[0.08px] tw:whitespace-nowrap" }, { children: "View stores" })) }))] })));
};
// StoreCard - Horizontal card for individual stores
const StoreCard = ({ store, onDetailsClick }) => {
    var _a, _b;
    // Full address
    const fullAddress = `${store.address}, ${store.city}, ${store.state} ${store.zipcode}`;
    return (jsxs("div", Object.assign({ className: "tw:flex-shrink-0 tw:p-2 sm:tw:p-3 tw:flex tw:items-center tw:gap-2 tw:cursor-pointer tw:transition-all hover:tw:shadow-md tw:snap-start tw:touch-manipulation", style: {
            fontFamily: 'Poppins, sans-serif',
            width: 'min(calc(100vw - 100px), 280px)',
            minHeight: '64px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #DCDDDC',
            borderRadius: '8px',
            boxShadow: `0 2px 6px ${getColorWithOpacity('--color-foreground', 0.08, '#000')}`,
        } }, { children: [jsx("div", Object.assign({ className: "tw:w-7 tw:h-7 sm:tw:w-8 sm:tw:h-8 tw:rounded-full tw:overflow-hidden tw:flex-shrink-0 tw:bg-[#F6F8FA]" }, { children: jsx("img", { src: getEntityImage(store.retailerid, 'retailer'), alt: (_a = store.storename) !== null && _a !== void 0 ? _a : undefined, className: "tw:w-full tw:h-full tw:object-contain" }) })), jsxs("div", Object.assign({ className: "tw:flex-1 tw:min-w-0 tw:overflow-hidden" }, { children: [jsx("p", Object.assign({ className: "tw:text-[10px] sm:tw:text-xs tw:font-medium tw:text-primary tw:m-0 tw:leading-[14px] sm:tw:leading-[16px] tw:tracking-[0.08px] tw:truncate" }, { children: ((_b = store.storename) === null || _b === void 0 ? void 0 : _b.split(' ')[0]) || 'Store' })), jsx("p", Object.assign({ className: "tw:text-xs sm:tw:text-sm tw:font-semibold tw:text-primary tw:m-0 tw:leading-[16px] sm:tw:leading-[20px] tw:tracking-[0.08px] tw:truncate" }, { children: store.storename || store.address })), jsx("p", Object.assign({ className: "tw:text-[10px] sm:tw:text-xs tw:font-medium tw:text-primary tw:m-0 tw:leading-[14px] sm:tw:leading-[16px] tw:tracking-[0.08px] tw:truncate" }, { children: fullAddress }))] })), jsx("button", Object.assign({ onClick: (e) => {
                    e.stopPropagation();
                    onDetailsClick();
                }, className: "tw:h-[32px] sm:tw:h-[36px] tw:min-h-[32px] sm:tw:min-h-[36px] tw:px-[10px] sm:tw:px-[12px] tw:py-[4px] tw:bg-[#F6F8FA] tw:rounded-[16px] sm:tw:rounded-[18px] tw:border-0 tw:cursor-pointer tw:flex-shrink-0 tw:flex tw:items-center tw:justify-center tw:touch-manipulation" }, { children: jsx("span", Object.assign({ className: "tw:text-[10px] sm:tw:text-xs tw:font-semibold tw:text-primary tw:leading-[16px] sm:tw:leading-[20px] tw:tracking-[0.08px]" }, { children: "Details" })) }))] })));
};
// StoreDetailPanel - Floating panel overlay on the right side of the map (full-screen on mobile)
const StoreDetailPanel = ({ store, details, loading, onClose }) => {
    var _a, _b, _c;
    return (jsxs("div", Object.assign({ className: "tw:absolute tw:bg-white tw:shadow-lg tw:overflow-hidden tw:flex tw:flex-col tw:z-10 tw:inset-0 sm:tw:inset-auto sm:tw:w-[300px] sm:tw:right-3 sm:tw:top-3 sm:tw:bottom-3 sm:tw:rounded-[10px]", style: { fontFamily: 'Poppins, sans-serif' } }, { children: [jsxs("div", Object.assign({ className: "tw:relative tw:h-[164px] tw:flex-shrink-0 tw:bg-[#F6F8FA]" }, { children: [jsx("img", { src: `https://maps.googleapis.com/maps/api/streetview?size=300x164&location=${(_a = store.location) === null || _a === void 0 ? void 0 : _a.lat},${(_b = store.location) === null || _b === void 0 ? void 0 : _b.lon}&key=${BrandclubStoreConfigUtils.getGoogleMapsApiKey()}`, alt: store.storename || 'Store', className: "tw:w-full tw:h-full tw:object-cover sm:tw:rounded-t-[10px]", onError: (e) => {
                            // Fallback to solid color if Street View fails
                            e.currentTarget.style.display = 'none';
                        } }), jsx("div", Object.assign({ className: "tw:absolute tw:top-4 tw:right-4" }, { children: jsx("div", Object.assign({ className: "tw:w-10 tw:h-10 tw:flex tw:items-center tw:justify-center sm:tw:w-auto sm:tw:h-auto" }, { children: jsx(CloseIcon, { onClick: onClose }) })) }))] })), jsxs("div", Object.assign({ className: "tw:flex-1 tw:overflow-auto tw:p-4" }, { children: [jsxs("div", Object.assign({ className: "tw:flex tw:items-start tw:gap-2 tw:mb-2" }, { children: [jsx("div", Object.assign({ className: "tw:w-8 tw:h-8 tw:rounded-full tw:overflow-hidden tw:flex-shrink-0 tw:bg-[#F6F8FA]" }, { children: jsx("img", { src: getEntityImage(store.retailerid, 'retailer'), alt: (_c = store.storename) !== null && _c !== void 0 ? _c : undefined, className: "tw:w-full tw:h-full tw:object-contain" }) })), jsx("h3", Object.assign({ className: "tw:text-lg tw:font-semibold tw:text-primary tw:m-0 tw:leading-[24px] tw:tracking-[0.08px]" }, { children: store.storename || 'Store' }))] })), loading ? (jsx("div", { className: "tw:h-4 tw:bg-[#F6F8FA] tw:rounded tw:animate-pulse tw:w-1/2 tw:mb-4" })) : (details === null || details === void 0 ? void 0 : details.openNow) !== undefined ? (jsxs("p", Object.assign({ className: "tw:text-xs tw:m-0 tw:leading-[16px] tw:tracking-[0.08px] tw:mb-4" }, { children: [jsx("span", Object.assign({ className: `tw:font-bold ${details.openNow ? 'tw:text-[#71c184]' : 'tw:text-red-500'}` }, { children: details.openNow ? 'Open' : 'Closed' })), details.openUntil && details.openNow && (jsxs("span", Object.assign({ className: "tw:text-primary tw:font-medium" }, { children: [" until ", details.openUntil, " today"] })))] }))) : null, jsxs("div", Object.assign({ className: "tw:space-y-3" }, { children: [jsx("p", Object.assign({ className: "tw:text-xs tw:font-bold tw:text-primary tw:m-0 tw:leading-[16px] tw:tracking-[0.08px]" }, { children: "Store information" })), jsxs("p", Object.assign({ className: "tw:text-xs tw:font-medium tw:text-primary tw:m-0 tw:leading-[16px] tw:tracking-[0.08px]" }, { children: [store.storename, jsx("br", {}), store.address, ", ", store.city, ", ", store.state, " ", store.zipcode] })), loading ? (jsx("div", { className: "tw:h-4 tw:bg-[#F6F8FA] tw:rounded tw:animate-pulse tw:w-1/3" })) : (details === null || details === void 0 ? void 0 : details.phone) ? (jsx("a", Object.assign({ href: `tel:${details.phone}`, className: "tw:text-xs tw:font-medium tw:text-primary tw:no-underline hover:tw:underline tw:block tw:leading-[16px] tw:tracking-[0.08px]" }, { children: details.phone }))) : null, loading ? (jsx("div", Object.assign({ className: "tw:space-y-2" }, { children: [1, 2, 3].map((i) => (jsx("div", { className: "tw:h-4 tw:bg-[#F6F8FA] tw:rounded tw:animate-pulse tw:w-full" }, i))) }))) : (details === null || details === void 0 ? void 0 : details.regularHours) && details.regularHours.length > 0 ? (jsx("div", { children: details.regularHours.map((h, i) => (jsxs("div", Object.assign({ className: "tw:flex tw:justify-between tw:text-xs tw:font-medium tw:text-primary" }, { children: [jsx("span", Object.assign({ className: "tw:leading-[16px] tw:tracking-[0.08px]" }, { children: h.day })), jsx("span", Object.assign({ className: "tw:leading-[16px] tw:tracking-[0.08px]" }, { children: h.hours }))] }), i))) })) : null, !loading && (details === null || details === void 0 ? void 0 : details.holidayHours) && details.holidayHours.length > 0 && (jsxs(Fragment, { children: [jsx("p", Object.assign({ className: "tw:text-xs tw:font-bold tw:text-primary tw:m-0 tw:mt-4 tw:leading-[16px] tw:tracking-[0.08px]" }, { children: "Holiday hours" })), jsx("div", { children: details.holidayHours.map((h, i) => (jsxs("div", Object.assign({ className: "tw:flex tw:justify-between tw:text-xs tw:font-medium tw:text-primary" }, { children: [jsx("span", Object.assign({ className: "tw:leading-[16px] tw:tracking-[0.08px]" }, { children: h.date })), jsx("span", Object.assign({ className: "tw:leading-[16px] tw:tracking-[0.08px]" }, { children: h.hours }))] }), i))) })] }))] }))] }))] })));
};
// Inner component that contains all the store locator logic
const StoreLocatorWidgetInner = ({ stacklineSku, apiUrl, widgetState, }) => {
    const [viewState, setViewState] = useState('retailer-summary');
    const [stores, setStores] = useState([]);
    const [retailerSummaries, setRetailerSummaries] = useState([]);
    const [selectedRetailerId, setSelectedRetailerId] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [storeDetails, setStoreDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [map, setMap] = useState(null);
    const [isMarkerLibraryLoaded, setIsMarkerLibraryLoaded] = useState(false);
    const markersRef = useRef([]);
    const { location, getLocation, hasCheckedPermission } = useGeolocation();
    const [initialCenter, setInitialCenter] = useState(null);
    const [lastFetchedPosition, setLastFetchedPosition] = useState(null);
    const carouselRef = useRef(null);
    const [isOnlineOnly, setIsOnlineOnly] = useState(false);
    const [hasAnyStores, setHasAnyStores] = useState(true);
    const [_totalStoresGlobally, setTotalStoresGlobally] = useState(0);
    // Track if user has manually panned the map (for UX: don't auto-recenter after manual pan)
    const [hasUserPanned, setHasUserPanned] = useState(false);
    const [hasAppliedBrowserLocation, setHasAppliedBrowserLocation] = useState(false);
    const hasFetchedInitialStores = useRef(false);
    // Search input and autocomplete state
    const [searchInput, setSearchInput] = useState('');
    const [predictions, setPredictions] = useState([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [sessionToken, setSessionToken] = useState(null);
    const searchWrapperRef = useRef(null);
    const googleMapsApiKey = BrandclubStoreConfigUtils.getGoogleMapsApiKey();
    const googleMapsMapId = BrandclubStoreConfigUtils.getGoogleMapsMapId();
    const API_URL = apiUrl || BrandclubStoreConfigUtils.getApiHostName();
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'bc-concierge-store-locator',
        googleMapsApiKey,
        libraries,
    });
    // Initialize AutocompleteSessionToken when Google Maps is loaded
    useEffect(() => {
        var _a, _b;
        if (isLoaded && ((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.places)) {
            const token = new window.google.maps.places.AutocompleteSessionToken();
            setSessionToken(token);
        }
    }, [isLoaded]);
    // Handle clicks outside search dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
                setShowPredictions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    // Get user's browser location on mount (may be denied or take time)
    useEffect(() => {
        getLocation();
    }, []);
    // If browser geolocation becomes available and user hasn't manually panned, recenter to it (once)
    useEffect(() => {
        if (location && // Browser location is available
            !hasUserPanned && // User hasn't manually moved the map
            !hasAppliedBrowserLocation && // We haven't already applied browser location
            map // Map is loaded
        ) {
            // Recenter map to browser location (more accurate than IP)
            map.panTo(location);
            map.setZoom(11);
            setInitialCenter(location);
            setHasAppliedBrowserLocation(true);
            // Also re-fetch stores for the new location
            fetchStores({
                userLat: location.lat,
                userLon: location.lng,
                radiusMiles: 50,
            });
        }
    }, [location, hasUserPanned, hasAppliedBrowserLocation, map]);
    // Fetch stores
    const fetchStores = useCallback((params) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        try {
            setLoading(true);
            const response = yield axios.post(`${API_URL}/store-locator/search`, {
                stacklineSku,
                userLat: params.userLat,
                userLon: params.userLon,
                radiusMiles: params.radiusMiles,
            });
            const storeData = ((_a = response.data) === null || _a === void 0 ? void 0 : _a.stores) || [];
            setStores(storeData);
            // Handle new response fields
            setIsOnlineOnly(((_b = response.data) === null || _b === void 0 ? void 0 : _b.isOnlineOnly) || false);
            setHasAnyStores(((_c = response.data) === null || _c === void 0 ? void 0 : _c.hasAnyStores) !== false); // Default to true if not provided
            setTotalStoresGlobally(((_d = response.data) === null || _d === void 0 ? void 0 : _d.totalStoresGlobally) || 0);
            // If this is the initial fetch and we got a detected location from IP, use it to center the map
            if (params.isInitialFetch && ((_e = response.data) === null || _e === void 0 ? void 0 : _e.detectedLocation) && !initialCenter) {
                const detected = response.data.detectedLocation;
                setInitialCenter({ lat: detected.lat, lng: detected.lng });
            }
            // Group stores by retailer
            const retailerMap = new Map();
            storeData.forEach((store) => {
                var _a;
                if (!retailerMap.has(store.retailerid)) {
                    retailerMap.set(store.retailerid, {
                        retailerId: store.retailerid,
                        retailerName: ((_a = store.storename) === null || _a === void 0 ? void 0 : _a.split(' ')[0]) || `Retailer ${store.retailerid}`,
                        storeCount: 0,
                        stores: [],
                    });
                }
                const summary = retailerMap.get(store.retailerid);
                if (summary) {
                    summary.storeCount++;
                    summary.stores.push(store);
                }
            });
            setRetailerSummaries(Array.from(retailerMap.values()));
        }
        catch (error) {
            console.error('Error fetching stores:', error);
            setStores([]);
            setRetailerSummaries([]);
            setIsOnlineOnly(false);
            setHasAnyStores(true);
            setTotalStoresGlobally(0);
        }
        finally {
            setLoading(false);
        }
    }), [API_URL, stacklineSku, initialCenter]);
    // Fetch store details
    const fetchStoreDetails = (store) => __awaiter(void 0, void 0, void 0, function* () {
        var _f, _g, _h;
        try {
            setDetailsLoading(true);
            const response = yield axios.post(`${API_URL}/store-locator/details`, {
                storeName: store.storename,
                address: store.address,
                city: store.city,
                state: store.state,
                zipcode: store.zipcode,
                lat: (_f = store.location) === null || _f === void 0 ? void 0 : _f.lat,
                lon: (_g = store.location) === null || _g === void 0 ? void 0 : _g.lon,
            });
            if ((_h = response.data) === null || _h === void 0 ? void 0 : _h.success) {
                setStoreDetails(response.data.details);
            }
            else {
                setStoreDetails(null);
            }
        }
        catch (error) {
            console.error('Error fetching store details:', error);
            setStoreDetails(null);
        }
        finally {
            setDetailsLoading(false);
        }
    });
    // Initial fetch - backend handles IP-based location fallback
    useEffect(() => {
        if (hasFetchedInitialStores.current)
            return;
        // If browser location is already available, use it
        if (location) {
            hasFetchedInitialStores.current = true;
            setInitialCenter(location);
            fetchStores({
                userLat: location.lat,
                userLon: location.lng,
                radiusMiles: 50,
                isInitialFetch: true,
            });
        }
        // Otherwise, let backend handle IP-based location
        else if (hasCheckedPermission) {
            hasFetchedInitialStores.current = true;
            fetchStores({
                radiusMiles: 50,
                isInitialFetch: true,
            });
        }
    }, [location, hasCheckedPermission, fetchStores]);
    // Debounced fetch for map pan
    const debouncedFetchStores = useCallback(_debounce((params) => {
        fetchStores(params);
    }, 500), [fetchStores]);
    // Handle map idle - detect manual panning
    const handleMapIdle = useCallback(() => {
        if (!map)
            return;
        const center = map.getCenter();
        if (!center)
            return;
        const newLat = center.lat();
        const newLng = center.lng();
        if (!lastFetchedPosition ||
            hasSignificantPositionChange(lastFetchedPosition.lat, lastFetchedPosition.lng, newLat, newLng)) {
            // If map has moved significantly from where we last fetched, user has manually panned
            // This prevents auto-recentering to browser location after user has explored the map
            if (lastFetchedPosition) {
                setHasUserPanned(true);
            }
            setLastFetchedPosition({ lat: newLat, lng: newLng });
            debouncedFetchStores({
                userLat: newLat,
                userLon: newLng,
                radiusMiles: 50,
            });
        }
    }, [map, lastFetchedPosition, debouncedFetchStores]);
    // Create marker element
    const createMarkerElement = (store, isSelected) => {
        const markerDiv = document.createElement('div');
        markerDiv.style.position = 'relative';
        markerDiv.style.cursor = 'pointer';
        markerDiv.style.transform = isSelected ? 'scale(1.15)' : 'scale(1)';
        markerDiv.style.transition = 'transform 0.2s ease';
        markerDiv.style.zIndex = isSelected ? '10' : '1';
        // Selected: use primary color, Unselected: white pin with border
        const pinFill = isSelected ? getCSSVariable('--color-primary', '#031425') : '#FFFFFF';
        const pinStroke = isSelected
            ? getCSSVariable('--color-primary', '#031425')
            : getCSSVariable('--color-border', '#DCDDDC');
        const strokeWidth = isSelected ? '0' : '1';
        // Create wrapper for the pin
        const pinWrapper = document.createElement('div');
        pinWrapper.style.position = 'relative';
        pinWrapper.style.width = '32px';
        pinWrapper.style.height = '40px';
        const shadowColor = getColorWithOpacity('--color-foreground', isSelected ? 0.3 : 0.15, '#000');
        pinWrapper.style.filter = isSelected
            ? `drop-shadow(0 2px 4px ${shadowColor})`
            : `drop-shadow(0 1px 2px ${shadowColor})`;
        pinWrapper.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
        <path d="M16 0C7.16 0 0 7.16 0 16c0 8.84 16 24 16 24s16-15.16 16-24C32 7.16 24.84 0 16 0z" fill="${pinFill}" stroke="${pinStroke}" stroke-width="${strokeWidth}"/>
        <circle cx="16" cy="14" r="11" fill="white"/>
      </svg>
    `;
        const logoContainer = document.createElement('div');
        logoContainer.style.position = 'absolute';
        logoContainer.style.top = isSelected ? '3px' : '5px';
        logoContainer.style.left = '5px';
        logoContainer.style.width = '22px';
        logoContainer.style.height = '22px';
        logoContainer.style.borderRadius = '50%';
        logoContainer.style.overflow = 'hidden';
        logoContainer.style.display = 'flex';
        logoContainer.style.alignItems = 'center';
        logoContainer.style.justifyContent = 'center';
        logoContainer.style.backgroundColor = '#FFFFFF';
        const logo = document.createElement('img');
        logo.src = getEntityImage(store.retailerid, 'retailer');
        logo.alt = store.storename || '';
        logo.style.width = '18px';
        logo.style.height = '18px';
        logo.style.objectFit = 'contain';
        logoContainer.appendChild(logo);
        pinWrapper.appendChild(logoContainer);
        markerDiv.appendChild(pinWrapper);
        return markerDiv;
    };
    // Update markers when stores change
    useEffect(() => {
        var _a, _b, _c;
        if (!map || !isMarkerLibraryLoaded || !((_c = (_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.marker) === null || _c === void 0 ? void 0 : _c.AdvancedMarkerElement))
            return;
        // Clear existing markers
        markersRef.current.forEach((marker) => {
            marker.map = null;
        });
        markersRef.current = [];
        // Get stores to display based on view state
        const displayStores = viewState === 'retailer-summary'
            ? stores
            : viewState === 'store-details'
                ? stores
                : selectedRetailerId
                    ? stores.filter((s) => s.retailerid === selectedRetailerId)
                    : stores;
        // Create new markers
        displayStores.forEach((store) => {
            var _a, _b;
            if (!((_a = store.location) === null || _a === void 0 ? void 0 : _a.lat) || !((_b = store.location) === null || _b === void 0 ? void 0 : _b.lon))
                return;
            const isSelected = selectedStore && selectedStore.address === store.address;
            const markerDiv = createMarkerElement(store, !!isSelected);
            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                map,
                position: { lat: store.location.lat, lng: store.location.lon },
                content: markerDiv,
                title: store.storename || undefined,
            });
            marker.addListener('click', () => {
                setSelectedStore(store);
                if (viewState === 'retailer-summary') {
                    setSelectedRetailerId(store.retailerid);
                    setViewState('store-list');
                }
                else if (viewState === 'store-list') {
                    // Open details when clicking marker in store list view
                    setViewState('store-details');
                    fetchStoreDetails(store);
                }
            });
            markersRef.current.push(marker);
        });
    }, [map, stores, selectedStore, isMarkerLibraryLoaded, viewState, selectedRetailerId]);
    // Handle map load
    const handleMapLoad = (loadedMap) => {
        setMap(loadedMap);
        setIsMarkerLibraryLoaded(false);
        setTimeout(() => {
            setIsMarkerLibraryLoaded(true);
        }, 0);
    };
    // Handle retailer selection
    const handleRetailerSelect = (retailerId) => {
        setSelectedRetailerId(retailerId);
        setViewState('store-list');
        // Pan to first store of selected retailer
        const firstStore = stores.find((s) => s.retailerid === retailerId);
        if (firstStore && map && firstStore.location) {
            map.panTo({ lat: firstStore.location.lat, lng: firstStore.location.lon });
            map.setZoom(12);
        }
    };
    // Handle store details click
    const handleStoreDetailsClick = (store) => {
        setSelectedStore(store);
        setViewState('store-details');
        fetchStoreDetails(store);
        if (map && store.location) {
            map.panTo({ lat: store.location.lat, lng: store.location.lon });
            map.setZoom(15);
        }
    };
    // Handle close detail panel
    const handleCloseDetails = () => {
        setViewState(selectedRetailerId ? 'store-list' : 'retailer-summary');
        setSelectedStore(null);
        setStoreDetails(null);
    };
    // Handle back to all retailers
    const handleBackToAllRetailers = () => {
        setSelectedRetailerId(null);
        setViewState('retailer-summary');
        setSelectedStore(null);
        setStoreDetails(null);
    };
    // Handle search input change
    const handleSearchInputChange = (event) => {
        var _a, _b;
        const value = event.target.value;
        setSearchInput(value);
        if (value && value.length >= 2 && sessionToken && ((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.places)) {
            // Try to use the new AutocompleteSuggestion API if available, otherwise fall back to AutocompleteService
            const useNewAPI = typeof window.google.maps.places.AutocompleteSuggestion !== 'undefined' &&
                typeof window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions === 'function';
            if (useNewAPI) {
                // New API: AutocompleteSuggestion.fetchAutocompleteSuggestions()
                const request = {
                    input: value,
                    sessionToken: sessionToken,
                    includedRegionCodes: ['us'],
                };
                window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
                    .then((response) => {
                    var _a;
                    if ((response === null || response === void 0 ? void 0 : response.suggestions) && response.suggestions.length > 0) {
                        // Convert AutocompleteSuggestion format to AutocompletePrediction format for compatibility
                        const predictions = [];
                        for (const suggestion of response.suggestions) {
                            if (suggestion.placePrediction) {
                                const placePrediction = suggestion.placePrediction;
                                const mainText = ((_a = placePrediction.text) === null || _a === void 0 ? void 0 : _a.text) || '';
                                const terms = mainText.split(', ');
                                let offset = 0;
                                predictions.push({
                                    place_id: placePrediction.placeId,
                                    description: mainText,
                                    structured_formatting: {
                                        main_text: mainText,
                                        secondary_text: '',
                                        main_text_matched_substrings: [],
                                    },
                                    terms: terms.map((term) => {
                                        const termObj = { value: term, offset };
                                        offset += term.length + 2; // +2 for ', '
                                        return termObj;
                                    }),
                                    types: [],
                                    matched_substrings: [],
                                });
                            }
                        }
                        setPredictions(predictions);
                        setShowPredictions(true);
                    }
                    else {
                        setPredictions([]);
                        setShowPredictions(false);
                    }
                })
                    .catch(() => {
                    setPredictions([]);
                    setShowPredictions(false);
                });
            }
            else {
                // Legacy API: AutocompleteService (fallback for older API versions)
                // Note: This API is deprecated but will continue to work
                const autocompleteService = new window.google.maps.places.AutocompleteService();
                autocompleteService.getPlacePredictions({
                    input: value,
                    sessionToken: sessionToken,
                    // Use componentRestrictions to limit to US cities/regions
                    componentRestrictions: { country: 'us' },
                }, (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        setPredictions(predictions);
                        setShowPredictions(true);
                    }
                    else {
                        setPredictions([]);
                        setShowPredictions(false);
                    }
                });
            }
        }
        else {
            setPredictions([]);
            setShowPredictions(false);
        }
    };
    // Handle prediction selection
    const handleSelectPrediction = (prediction) => __awaiter(void 0, void 0, void 0, function* () {
        var _j, _k, _l, _m, _o;
        setSearchInput(prediction.description);
        setPredictions([]);
        setShowPredictions(false);
        // Try to use the new Place API if available, otherwise fall back to PlacesService
        const useNewPlaceAPI = typeof ((_l = (_k = (_j = window.google) === null || _j === void 0 ? void 0 : _j.maps) === null || _k === void 0 ? void 0 : _k.places) === null || _l === void 0 ? void 0 : _l.Place) !== 'undefined' &&
            typeof window.google.maps.places.Place.prototype.fetchFields === 'function';
        if (useNewPlaceAPI && ((_o = (_m = window.google) === null || _m === void 0 ? void 0 : _m.maps) === null || _o === void 0 ? void 0 : _o.places)) {
            // New API: Place class
            try {
                const place = new window.google.maps.places.Place({ id: prediction.place_id });
                yield place.fetchFields({ fields: ['location'] });
                if (place.location && map) {
                    const lat = place.location.lat();
                    const lng = place.location.lng();
                    // Pan map to selected location
                    map.panTo({ lat, lng });
                    map.setZoom(11);
                    // Fetch stores for the new location
                    fetchStores({
                        userLat: lat,
                        userLon: lng,
                        radiusMiles: 50,
                    });
                    // Create new session token for next search
                    const newToken = new window.google.maps.places.AutocompleteSessionToken();
                    setSessionToken(newToken);
                }
            }
            catch (error) {
                console.error('Error fetching place details with new API:', error);
                // Fall back to legacy API on error
                fetchPlaceDetailsLegacy(prediction.place_id);
            }
        }
        else {
            // Legacy API: PlacesService (fallback for older API versions)
            fetchPlaceDetailsLegacy(prediction.place_id);
        }
    });
    // Helper function for legacy PlacesService API
    const fetchPlaceDetailsLegacy = (placeId) => {
        var _a, _b;
        if (!((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.places) || !map)
            return;
        const serviceDiv = document.createElement('div');
        const placesService = new window.google.maps.places.PlacesService(serviceDiv);
        placesService.getDetails({
            placeId: placeId,
            fields: ['geometry'],
        }, (place, status) => {
            var _a, _b, _c;
            if (status === window.google.maps.places.PlacesServiceStatus.OK && ((_a = place === null || place === void 0 ? void 0 : place.geometry) === null || _a === void 0 ? void 0 : _a.location) && map) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                // Pan map to selected location
                map.panTo({ lat, lng });
                map.setZoom(11);
                // Fetch stores for the new location
                fetchStores({
                    userLat: lat,
                    userLon: lng,
                    radiusMiles: 50,
                });
                // Create new session token for next search
                if ((_c = (_b = window.google) === null || _b === void 0 ? void 0 : _b.maps) === null || _c === void 0 ? void 0 : _c.places) {
                    const newToken = new window.google.maps.places.AutocompleteSessionToken();
                    setSessionToken(newToken);
                }
            }
        });
    };
    // Handle search on Enter key
    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter' && predictions.length > 0) {
            handleSelectPrediction(predictions[0]);
        }
    };
    if (loadError) {
        console.error('Google Maps load error:', loadError);
        return (jsx("div", Object.assign({ className: "tw:w-full tw:h-[400px] tw:flex tw:items-center tw:justify-center tw:bg-[#F6F8FA] tw:rounded-lg tw:border tw:border-[#DCDDDC]", style: { fontFamily: 'Poppins, sans-serif' } }, { children: jsx("p", Object.assign({ className: "tw:text-sm tw:text-red-500" }, { children: "Unable to load store locator. Please try again later." })) })));
    }
    const filteredStores = selectedRetailerId ? stores.filter((s) => s.retailerid === selectedRetailerId) : stores;
    // Debug flag - set to true to show debug info
    const SHOW_DEBUG = false;
    return (jsxs("div", Object.assign({ className: "tw:w-full tw:overflow-hidden tw:bg-white tw:my-3 tw:relative tw:mb-6", id: "store-locator-widget", style: { fontFamily: 'Poppins, sans-serif' } }, { children: [SHOW_DEBUG , jsxs("div", Object.assign({ className: "tw:relative tw:w-full", style: { height: 'min(400px, 50vh)' } }, { children: [!loading && isOnlineOnly ? (jsx("div", Object.assign({ className: "tw:w-full tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-[#F6F8FA] tw:rounded-lg tw:border tw:border-[#DCDDDC]" }, { children: jsxs("div", Object.assign({ className: "tw:text-center tw:px-4" }, { children: [jsx("p", Object.assign({ className: "tw:text-sm tw:font-medium tw:text-primary tw:mb-2" }, { children: "This product is only available online" })), jsx("p", Object.assign({ className: "tw:text-xs tw:text-primary/70" }, { children: "This product is not sold in brick-and-mortar stores. Check the product card above for online purchase options." }))] })) }))) : (
                    /* Google Map - fills the entire container, always shown when product has stores */
                    jsxs(Fragment, { children: [isLoaded ? (jsx(GoogleMap, { mapContainerStyle: { width: '100%', height: '100%' }, center: initialCenter || { lat: 39.8283, lng: -98.5795 }, zoom: location ? 11 : 4, onLoad: handleMapLoad, onIdle: handleMapIdle, options: {
                                    mapId: googleMapsMapId,
                                    zoomControl: true,
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                    fullscreenControl: false,
                                } })) : (jsx("div", Object.assign({ className: "tw:w-full tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-[#F6F8FA]" }, { children: jsx("div", { className: "tw:animate-spin tw:w-8 tw:h-8 tw:border-2 tw:border-primary tw:border-t-transparent tw:rounded-full" }) }))), !loading && hasAnyStores && stores.length === 0 && (jsx("div", Object.assign({ className: "tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:z-10 tw:pointer-events-none", style: { fontFamily: 'Poppins, sans-serif' } }, { children: jsxs("div", Object.assign({ className: "tw:bg-white/95 tw:backdrop-blur-sm tw:rounded-lg tw:px-4 tw:py-3 tw:text-center", style: {
                                        boxShadow: `0 2px 8px ${getColorWithOpacity('--color-foreground', 0.15, '#000')}`,
                                        maxWidth: '280px',
                                    } }, { children: [jsx("p", Object.assign({ className: "tw:text-sm tw:font-medium tw:text-primary tw:mb-1" }, { children: "No stores found in this area" })), jsx("p", Object.assign({ className: "tw:text-xs tw:text-primary/70" }, { children: "Try zooming out or searching a different location" }))] })) })))] })), isLoaded && !isOnlineOnly && (jsxs(Fragment, { children: [jsx("div", Object.assign({ className: "tw:absolute tw:z-10", style: { top: '12px', left: '12px' }, ref: searchWrapperRef }, { children: jsxs("div", Object.assign({ className: "tw:relative", style: {
                                        fontFamily: 'Poppins, sans-serif',
                                    } }, { children: [jsxs("div", Object.assign({ className: "tw:flex tw:items-center tw:gap-[12px]", style: {
                                                backgroundColor: '#FFFFFF',
                                                border: `1px solid ${getCSSVariable('--color-border', '#DCDDDC')}`,
                                                borderRadius: '100px',
                                                height: '44px',
                                                width: 'min(calc(100vw - 48px), 352px)',
                                                paddingLeft: '16px',
                                                paddingRight: '16px',
                                                boxShadow: `0 2px 6px ${getColorWithOpacity('--color-foreground', 0.1, '#000')}`,
                                            } }, { children: [jsx("div", Object.assign({ className: "tw:flex tw:items-center tw:justify-center tw:flex-shrink-0", style: { width: '16px', height: '16px', marginRight: '8px' } }, { children: jsx(SearchIcon, { className: "tw:text-primary/60" }) })), jsx("input", { type: "text", placeholder: "Search location...", value: searchInput, onChange: handleSearchInputChange, onKeyDown: handleSearchKeyDown, onFocus: () => {
                                                        if (predictions.length > 0) {
                                                            setShowPredictions(true);
                                                        }
                                                    }, className: "tw:flex-1 tw:border-0 tw:outline-none tw:text-sm tw:font-medium tw:text-primary/50 tw:bg-transparent", style: {
                                                        fontFamily: 'Poppins, sans-serif',
                                                        lineHeight: '1.5',
                                                        padding: '0',
                                                        margin: '0',
                                                        height: 'auto',
                                                        verticalAlign: 'middle',
                                                    }, autoComplete: "off" })] })), showPredictions && predictions.length > 0 && (jsx("div", Object.assign({ className: "tw:absolute tw:top-[48px] tw:left-0 tw:w-full tw:bg-white tw:rounded-lg tw:shadow-lg tw:overflow-hidden tw:z-20", style: {
                                                border: `1px solid ${getCSSVariable('--color-border', '#DCDDDC')}`,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                backgroundColor: getCSSVariable('--color-background', '#FFFFFF'),
                                            } }, { children: predictions.map((prediction, index) => (jsx("div", Object.assign({ onClick: () => handleSelectPrediction(prediction), className: "tw:px-4 tw:py-2 tw:cursor-pointer tw:text-xs tw:font-medium", style: {
                                                    fontFamily: 'Poppins, sans-serif',
                                                    color: getCSSVariable('--color-foreground', '#000'),
                                                    borderBottom: index < predictions.length - 1
                                                        ? `1px solid ${getCSSVariable('--color-border', '#DCDDDC')}`
                                                        : 'none',
                                                    transition: 'background-color 0.15s ease',
                                                }, onMouseEnter: (e) => {
                                                    e.currentTarget.style.backgroundColor = getCSSVariable('--color-accent', '#F5F5F5');
                                                }, onMouseLeave: (e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                } }, { children: prediction.description }), prediction.place_id))) })))] })) })), viewState !== 'store-details' && (jsxs("div", Object.assign({ className: "tw:absolute tw:z-10", style: { bottom: '12px', left: '12px', right: '60px' } }, { children: [viewState === 'store-list' && !loading && (jsx("div", Object.assign({ className: "tw:mb-3" }, { children: jsxs("button", Object.assign({ onClick: handleBackToAllRetailers, className: "tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-2 tw:min-h-[44px] tw:rounded-full tw:bg-white tw:border tw:cursor-pointer tw:transition-colors tw:touch-manipulation", style: {
                                                fontFamily: 'Poppins, sans-serif',
                                                border: `1px solid ${getCSSVariable('--color-border', '#DCDDDC')}`,
                                                boxShadow: `0 2px 6px ${getColorWithOpacity('--color-foreground', 0.1, '#000')}`,
                                                color: getCSSVariable('--color-foreground', '#000'),
                                            }, onMouseEnter: (e) => {
                                                e.currentTarget.style.backgroundColor = getCSSVariable('--color-accent', '#F5F5F5');
                                            }, onMouseLeave: (e) => {
                                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                                            } }, { children: [jsx("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", style: { flexShrink: 0 } }, { children: jsx("path", { d: "M10 12L6 8L10 4", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) })), jsx("span", Object.assign({ className: "tw:text-xs tw:font-semibold tw:tracking-[0.08px]" }, { children: "View all retailers" }))] })) }))), loading ? (jsx("div", Object.assign({ className: "tw:flex tw:gap-3 tw:overflow-x-auto tw:pb-2 tw:scrollbar-hide" }, { children: [1, 2, 3].map((i) => (jsx("div", { className: "tw:flex-shrink-0 tw:animate-pulse", style: {
                                                width: 'min(calc(100vw - 100px), 280px)',
                                                height: '64px',
                                                backgroundColor: '#FFFFFF',
                                                border: '1px solid #DCDDDC',
                                                borderRadius: '8px',
                                                boxShadow: `0 2px 6px ${getColorWithOpacity('--color-foreground', 0.08, '#000')}`,
                                            } }, i))) }))) : viewState === 'retailer-summary' ? (jsxs("div", Object.assign({ ref: carouselRef, className: "tw:flex tw:gap-3 tw:overflow-x-auto tw:pb-2 tw:scrollbar-hide tw:snap-x tw:snap-mandatory tw:touch-pan-x", style: {
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            WebkitOverflowScrolling: 'touch',
                                        } }, { children: [retailerSummaries.map((summary) => (jsx(RetailerSummaryCard, { retailerSummary: summary, onClick: () => handleRetailerSelect(summary.retailerId) }, summary.retailerId))), retailerSummaries.length === 0 && (jsx("div", Object.assign({ className: "tw:w-full tw:flex tw:items-center tw:justify-center", style: {
                                                    height: '88px',
                                                    backgroundColor: '#FFFFFF',
                                                    border: '1px solid #DCDDDC',
                                                    borderRadius: '8px',
                                                    boxShadow: `0 2px 6px ${getColorWithOpacity('--color-foreground', 0.08, '#000')}`,
                                                } }, { children: jsx("p", Object.assign({ className: "tw:text-sm tw:text-[#666]" }, { children: "No stores found nearby" })) })))] }))) : (jsx("div", Object.assign({ ref: carouselRef, className: "tw:flex tw:gap-3 tw:overflow-x-auto tw:pb-2 tw:scrollbar-hide tw:snap-x tw:snap-mandatory tw:touch-pan-x", style: {
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            WebkitOverflowScrolling: 'touch',
                                        } }, { children: filteredStores.map((store) => (jsx(StoreCard, { store: store, onDetailsClick: () => handleStoreDetailsClick(store) }, `${store.retailerid}-${store.address}-${store.city}`))) })))] }))), viewState === 'store-details' && selectedStore && (jsx(StoreDetailPanel, { store: selectedStore, details: storeDetails, loading: detailsLoading, onClose: handleCloseDetails }))] }))] }))] })));
};
// Main StoreLocatorWidget component - wrapped with bc-widgets for theme customization
const StoreLocatorWidget = ({ stacklineSku, apiUrl }) => {
    return (jsx(StoreLocatorWidget$1, Object.assign({ widgetId: "store-locator", displayName: "Store Locator" }, { children: (state, resolveColor) => (jsx(StoreLocatorStyleContext.Provider, Object.assign({ value: state }, { children: jsx(StoreLocatorWidgetInner, { stacklineSku: stacklineSku, apiUrl: apiUrl, widgetState: state }) }))) })));
};

export { StoreLocatorWidget, StoreLocatorWidget as default };
//# sourceMappingURL=index.js.map
